import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog/Dialog';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { Stepper } from '@/components/ui/Stepper/Stepper';
import { SchedulingPausedStep } from '@/features/scheduling/components/SchedulingPaused/SchedulingPausedStep';
import { useIsSchedulingBlocked } from '@/features/systemBanner/hooks/useIsSchedulingBlocked';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleFlowProvider } from './context/ScheduleFlowContext';
import { useScheduleFlow } from './context/useScheduleFlow';
import { DateAndTimeStep } from './steps/DateAndTimeStep/DateAndTimeStep';
import { ErrorStep } from './steps/ErrorStep/ErrorStep';
import { NoSessionsLeftStep } from './steps/NoSessionsLeftStep/NoSessionsLeftStep';
import { SelectStudentsStep } from './steps/SelectStudentsStep/SelectStudentsStep';
import { SessionDetailsStep } from './steps/SessionDetailsStep/SessionDetailsStep';
import { SuccessStep } from './steps/SuccessStep/SuccessStep';
import { SummaryStep } from './steps/SummaryStep/SummaryStep';

const ScheduleDialogContent = ({ onClose, isOpened }: { onClose: () => void; isOpened: boolean }) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog' });
  const { translateError } = useErrorTranslation();

  const { currentStep, resetFlow, errorType, data } = useScheduleFlow();
  const isSchedulingBlocked = useIsSchedulingBlocked();

  useEffect(() => {
    if (isOpened) {
      resetFlow();
    }
  }, [isOpened, resetFlow]);

  const getStepInfo = () => {
    switch (currentStep) {
      case 'sessionDetails': {
        return { currentStep: 0, totalSteps: 3, showStepper: true };
      }
      case 'dateAndTime': {
        return { currentStep: 1, totalSteps: 3, showStepper: true };
      }
      case 'summary': {
        return { currentStep: 2, totalSteps: 3, showStepper: true };
      }
      default: {
        return { currentStep: 0, totalSteps: 3, showStepper: false };
      }
    }
  };

  const stepInfo = getStepInfo();

  const getContent = () => {
    // A session that was already booked still shows its success step
    if (isSchedulingBlocked && currentStep !== 'success') {
      return <SchedulingPausedStep onClose={onClose} />;
    }

    if (errorType) {
      return <ErrorStep onClose={onClose} />;
    }

    if (data.config.isLoading) {
      return (
        <div className='flex flex-1 items-center justify-center p-6' role='status' aria-live='polite'>
          <Spinner className='size-20 stroke-1' />
        </div>
      );
    }

    if (data.config.error) {
      return (
        <div className='flex flex-1 items-center justify-center p-6' role='alert' aria-live='assertive'>
          <p className='text-text-default-secondary'>{translateError(data.config.error)}</p>
        </div>
      );
    }

    switch (currentStep) {
      case 'selectStudents': {
        return <SelectStudentsStep />;
      }
      case 'noSessionsLeft': {
        return <NoSessionsLeftStep onClose={onClose} />;
      }
      case 'sessionDetails': {
        return <SessionDetailsStep />;
      }
      case 'dateAndTime': {
        return <DateAndTimeStep portalContainerRef={dialogContainer} onClose={onClose} />;
      }
      case 'summary': {
        return <SummaryStep portalContainerRef={dialogContainer} onClose={onClose} />;
      }
      case 'success': {
        return <SuccessStep onClose={onClose} />;
      }
      case 'error': {
        return <ErrorStep onClose={onClose} />;
      }
    }
  };

  const dialogContainer = useRef<HTMLDivElement | null>(null);

  const getTitle = () => {
    switch (currentStep) {
      case 'success': {
        return t('successTitle');
      }
      default: {
        return t('title');
      }
    }
  };

  return (
    <DialogContent ref={dialogContainer} className='desktop:!max-h-[840px]'>
      <DialogHeader>
        <DialogTitle>{getTitle()}</DialogTitle>
        {stepInfo.showStepper && <Stepper steps={stepInfo.totalSteps} currentStep={stepInfo.currentStep} className='my-1' />}
      </DialogHeader>
      <DialogDescription className='sr-only'>{t('description')}</DialogDescription>
      {getContent()}
    </DialogContent>
  );
};

interface ScheduleDialogProps {
  isOpened: boolean;
  onOpenChange: (open: boolean) => void;
  openButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export const ScheduleDialog = ({ isOpened, onOpenChange, openButtonRef }: ScheduleDialogProps) => {
  const closeDialog = () => {
    void captureAnalyticsEvent('scheduling_closed');
    onOpenChange(false);

    if (openButtonRef?.current) {
      setTimeout(() => {
        openButtonRef.current?.focus();
      }, 0);
    }
  };

  return (
    <Dialog open={isOpened} onOpenChange={closeDialog}>
      <ScheduleFlowProvider>
        <ScheduleDialogContent onClose={closeDialog} isOpened={isOpened} />
      </ScheduleFlowProvider>
    </Dialog>
  );
};
